import { useMutation } from '@tanstack/react-query';
import { loginSuccess, useAppDispatch } from '@doccareplus/kernel/application';
import { authApi, type AuthResponse } from '@doccareplus/kernel/infrastructure/api/auth.service';

// Patient portal auth is passwordless phone + WhatsApp OTP (see .ai/BusinessDomain/authentication.md). These
// hooks reuse the shared kernel authApi and push the resulting session into the global auth slice. There is no
// clinic context — memberships come back empty and no clinic headers are set.

/** Send a WhatsApp OTP for signup or login. Recipient is an E.164 phone number. */
export function useSendOtpCommand() {
  return useMutation<void, unknown, { phoneE164: string; purpose: 'Signup' | 'Login' }>({
    mutationFn: ({ phoneE164, purpose }) =>
      authApi.sendOtp({ channel: 'WhatsApp', recipient: phoneE164, purpose }),
  });
}

/** Passwordless phone + OTP login for an existing patient account. */
export function useLoginWithOtpCommand() {
  const dispatch = useAppDispatch();
  return useMutation<AuthResponse, unknown, { phoneE164: string; otpCode: string }>({
    mutationFn: ({ phoneE164, otpCode }) => authApi.login({ phoneNumber: phoneE164, otpCode }),
    onSuccess: (auth) =>
      dispatch(loginSuccess({ user: auth.user, tokens: auth.tokens, memberships: auth.memberships })),
  });
}

/** Passwordless phone + OTP patient signup. */
export function useRegisterPatientCommand() {
  const dispatch = useAppDispatch();
  return useMutation<AuthResponse, unknown, { phoneE164: string; otpCode: string; fullName: string }>({
    mutationFn: ({ phoneE164, otpCode, fullName }) =>
      authApi.registerPatient({ phoneNumber: phoneE164, otpCode, fullName }),
    onSuccess: (auth) =>
      dispatch(loginSuccess({ user: auth.user, tokens: auth.tokens, memberships: auth.memberships })),
  });
}
